import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Slider, Select, MenuItem, FormControl, InputLabel, Button } from '@mui/material';
import { SketchPicker } from 'react-color';

const LayerSettings = ({ selectedLayerId, layers, onUpdateLayer }) => {
    const [currentLayer, setCurrentLayer] = useState(null);

    useEffect(() => {
        const layer = layers.find(layer => layer.id === selectedLayerId);
        setCurrentLayer(layer);
    }, [selectedLayerId, layers]);

    const handleChange = (field, value) => {
        if (currentLayer) {
            if (field === 'src') {
                const image = new Image();
                image.onload = () => {
                    let newWidth = image.width;
                    let newHeight = image.height;
                    const maxWidth = 300;
                    const maxHeight = 200;
                    if (image.width > maxWidth || image.height > maxHeight) {
                        const ratio = Math.min(maxWidth / image.width, maxHeight / image.height);
                        newWidth = image.width * ratio;
                        newHeight = image.height * ratio;
                    }

                    onUpdateLayer(currentLayer.id, { ...currentLayer, [field]: value, width: newWidth, height: newHeight });
                    setCurrentLayer(prev => ({ ...prev, [field]: value, width: newWidth, height: newHeight }));
                };
                image.src = value;

            } else {
                onUpdateLayer(currentLayer.id, { ...currentLayer, [field]: value });
                setCurrentLayer(prev => ({ ...prev, [field]: value }));
            }
        }
    };

     const handleReset = () => {
        if (currentLayer) {
            let resetLayer = { ...currentLayer, rotation: 0}
            if(currentLayer.type === 'image'){
             const image = new Image();
             image.onload = () => {
                let newWidth = image.width;
                    let newHeight = image.height;
                    const maxWidth = 300;
                    const maxHeight = 200;
                    if (image.width > maxWidth || image.height > maxHeight) {
                        const ratio = Math.min(maxWidth / image.width, maxHeight / image.height);
                        newWidth = image.width * ratio;
                        newHeight = image.height * ratio;
                    }
               onUpdateLayer(currentLayer.id, { ...resetLayer, width: newWidth, height: newHeight });
                  setCurrentLayer(prev => ({ ...resetLayer, width: newWidth, height: newHeight }));
              };
                image.src = currentLayer.src;
            } else {
                 onUpdateLayer(currentLayer.id, resetLayer);
                 setCurrentLayer(prev => ({ ...resetLayer }));
            }
        }
    };


    if (!currentLayer) {
        return <Box sx={{ width: "300px", padding: '16px' }}>Select a layer to edit.</Box>;
    }
    return (
        <Box sx={{ width: "300px", padding: '16px' }}>
            {currentLayer.type === 'text' && (
                <>
                    <Typography variant="h6">Text Layer Settings</Typography>
                    <TextField
                        label="Text"
                        value={currentLayer.text}
                        onChange={(e) => handleChange('text', e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="font-family-label">Font Family</InputLabel>
                        <Select
                            labelId="font-family-label"
                            id="font-family-select"
                            value={currentLayer.fontFamily}
                            label="Font Family"
                            onChange={(e) => handleChange('fontFamily', e.target.value)}
                        >
                            <MenuItem value="Arial">Arial</MenuItem>
                            <MenuItem value="Helvetica">Helvetica</MenuItem>
                            <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                            <MenuItem value="Courier New">Courier New</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="body1" >Font Size</Typography>
                    <Slider
                        value={currentLayer.fontSize}
                        min={12}
                        max={72}
                        step={1}
                        onChange={(e) => handleChange('fontSize', parseInt(e.target.value, 10))}
                        valueLabelDisplay="auto"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="font-style-label">Style</InputLabel>
                        <Select
                            labelId="font-style-label"
                            id="font-style-select"
                            value={currentLayer.style}
                            label="Style"
                            onChange={(e) => handleChange('style', e.target.value)}
                        >
                            <MenuItem value="normal">Normal</MenuItem>
                            <MenuItem value="bold">Bold</MenuItem>
                            <MenuItem value="border">Border</MenuItem>
                            <MenuItem value="shadow">Shadow</MenuItem>
                            <MenuItem value="neon">Neon</MenuItem>
                        </Select>
                    </FormControl>
                    {currentLayer.style === 'border' && (
                        <>
                            <Typography variant="body1">Border Color</Typography>
                            <SketchPicker color={currentLayer.borderColor}
                                onChange={(color) => handleChange('borderColor', color.hex)} />
                            <Typography variant="body1" >Border Width</Typography>
                            <Slider
                                value={currentLayer.borderWidth}
                                min={1}
                                max={10}
                                step={1}
                                onChange={(e) => handleChange('borderWidth', parseInt(e.target.value, 10))}
                                valueLabelDisplay="auto"
                            />

                        </>
                    )}
                    {currentLayer.style === 'shadow' && (
                        <>
                            <Typography variant="body1">Shadow Color</Typography>
                            <SketchPicker color={currentLayer.shadowColor}
                                onChange={(color) => handleChange('shadowColor', color.hex)} />
                            <Typography variant="body1" >Shadow Blur</Typography>
                            <Slider
                                value={currentLayer.shadowBlur}
                                min={0}
                                max={20}
                                step={1}
                                onChange={(e) => handleChange('shadowBlur', parseInt(e.target.value, 10))}
                                valueLabelDisplay="auto"
                            />
                            <Typography variant="body1">Shadow X Offset</Typography>
                            <Slider
                                value={currentLayer.shadowOffsetX}
                                min={-20}
                                max={20}
                                step={1}
                                onChange={(e) => handleChange('shadowOffsetX', parseInt(e.target.value, 10))}
                                valueLabelDisplay="auto"
                            />
                            <Typography variant="body1">Shadow Y Offset</Typography>
                            <Slider
                                value={currentLayer.shadowOffsetY}
                                min={-20}
                                max={20}
                                step={1}
                                onChange={(e) => handleChange('shadowOffsetY', parseInt(e.target.value, 10))}
                                valueLabelDisplay="auto"
                            />
                        </>
                    )}
                    <Typography variant="body1" >Text Color</Typography>
                    <SketchPicker color={currentLayer.color} onChange={(color) => handleChange('color', color.hex)} />
                    <Typography variant="body1">Opacity</Typography>
                    <Slider
                        value={currentLayer.opacity}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={(e) => handleChange('opacity', parseFloat(e.target.value))}
                        valueLabelDisplay="auto"
                    />
                </>
            )}
            {currentLayer.type === 'image' && (
                <>
                    <Typography variant="h6">Image Layer Settings</Typography>
                    <Typography variant="body1">Opacity</Typography>
                    <Slider
                        value={currentLayer.opacity}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={(e) => handleChange('opacity', parseFloat(e.target.value))}
                        valueLabelDisplay="auto"
                    />
                    <Typography variant="body1">Width</Typography>
                    <Slider
                        value={currentLayer.width}
                        min={5}
                         max={600}
                        step={1}
                        onChange={(e) => handleChange('width', parseInt(e.target.value))}
                        valueLabelDisplay="auto"
                    />
                    <Typography variant="body1">Height</Typography>
                    <Slider
                        value={currentLayer.height}
                        min={5}
                        max={400}
                        step={1}
                        onChange={(e) => handleChange('height', parseInt(e.target.value))}
                        valueLabelDisplay="auto"
                    />
                    <input type="file" accept="image/*" onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                handleChange('src', reader.result);
                            }
                            reader.readAsDataURL(file);
                        }
                    }
                    } />
                </>
            )}
             <Button variant="contained" onClick={handleReset}>Reset</Button>
        </Box>
    );
};

export default LayerSettings;